import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { Business as BusinessIcon, Gavel as GavelIcon, LocalPostOffice as MailIcon } from '@mui/icons-material';
import FormSection from './FormSection.jsx';
import FormSelect from './FormSelect.jsx';
import SimpleInput from '../Input/SimpleInput.jsx';
import CustomButton from '../Buttons/CustomButton';
import {
  useLazyGetLeadsForMerchantQuery,
  useLazyGetStatesQuery,
  useLazyGetUnitedStatesIdQuery,
  useLazyGetLeadByIdQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';

const MainStep = ({ merchantData, isEditMode, missingItems, formData = {}, updateFormData, validationErrors = {}, sourceType = 'normal', selectedLeadId = '' }) => {
  const [fetchLeadsForMerchant] = useLazyGetLeadsForMerchantQuery();
  const [fetchStates] = useLazyGetStatesQuery();
  const [fetchUnitedStatesId] = useLazyGetUnitedStatesIdQuery();
  const [fetchLeadById] = useLazyGetLeadByIdQuery();

  const leadSelect = useLazySelectOptions(fetchLeadsForMerchant, {
    args: {},
    enabled: sourceType === 'normal',
    mapResult: (res) => {
      const data = extractList(res?.data ?? res);
      if (!Array.isArray(data)) return [];
      return data.map((lead) => ({
        value: lead.id,
        label: lead.name,
      }));
    },
  });

  const [usCountryId, setUsCountryId] = useState(null);
  const pendingStateLoadRef = useRef(false);

  const stateSelect = useLazySelectOptions(fetchStates, {
    args: usCountryId,
    enabled: Boolean(usCountryId),
    mapResult: (states) => {
      const data = Array.isArray(states) ? states : (states?.states ?? []);
      return data.map((item) => ({ value: item?.id, label: item?.name }));
    },
  });

  const stateOptions = stateSelect.options;
  const [dbaName, setDbaName] = useState(formData?.dba_name || merchantData?.merchant_dba || '');
  const [ssnNumber, setSsnNumber] = useState(formData?.ssn || merchantData?.ssn || '');

  const [contactName, setContactName] = useState(formData?.contact_name || merchantData?.contact_person || '');
  const [contactPhone, setContactPhone] = useState(formData?.contact_phone || merchantData?.phone || '');
  const [address, setAddress] = useState(formData?.dba_address?.street || merchantData?.dba_address?.street || '');
  const [city, setCity] = useState(formData?.dba_address?.city || merchantData?.dba_address?.city || '');
  const [state, setState] = useState(formData?.dba_address?.state || merchantData?.dba_address?.state || '');
  const [zip, setZip] = useState(formData?.dba_address?.zip_code || merchantData?.dba_address?.zip || '');
  const [dbaPhone, setDbaPhone] = useState(formData?.dba_phone || merchantData?.phone || '');
  const [dbaEmail, setDbaEmail] = useState(formData?.dba_email || merchantData?.email || '');
  const [faxNumber, setFaxNumber] = useState(formData?.fax_number || merchantData?.fax_number || '');
  const [legalName, setLegalName] = useState(formData?.legal_name || merchantData?.legal_name || '');
  const [countryCode, setCountryCode] = useState(formData?.country_code || merchantData?.country_code || '');
  const [legalAddress, setLegalAddress] = useState(formData?.legal_address?.street || merchantData?.legal_address?.street || '');
  const [legalCity, setLegalCity] = useState(formData?.legal_address?.city || merchantData?.legal_address?.city || '');
  const [legalState, setLegalState] = useState(formData?.legal_address?.state || merchantData?.legal_address?.state || '');
  const [legalZip, setLegalZip] = useState(formData?.legal_address?.zip_code || merchantData?.legal_address?.zip || '');
  const [legalPhone, setLegalPhone] = useState(formData?.legal_phone || merchantData?.legal_phone || '');
  const [legalEmail, setLegalEmail] = useState(formData?.legal_email || merchantData?.legal_email || '');
  const [legalFaxNumber, setLegalFaxNumber] = useState(formData?.legal_fax_number || merchantData?.legal_fax_number || '');
  const [submissionDate, setSubmissionDate] = useState(formData?.submission_date || '');
  const [monthlyVolume, setMonthlyVolume] = useState(formData?.monthly_volume || '');
  const [monthlyLease, setMonthlyLease] = useState(formData?.monthly_lease || '');
  const [isBundled, setIsBundled] = useState(Boolean(formData?.is_bundled));
  const [processingOffered, setProcessingOffered] = useState(formData?.processing_offered || '');
  const [highestTicket, setHighestTicket] = useState(formData?.highest_ticket || '');
  const [fnsNumberForEbt, setFnsNumberForEbt] = useState(formData?.fns_number_for_ebt || '');
  // const [selectedLead, setSelectedLead] = useState(formData?.lead_id || ''); // New state for lead selection

  const isInitialized = useRef(false);
  const [selectedLead, setSelectedLead] = useState(formData?.lead?.name || selectedLeadId || '');

  const handleLeadSelectOpen = useCallback(() => {
    leadSelect.load().catch((error) => {
      const msg = typeof error === 'string' ? error : (error?.message || 'Failed to fetch leads');
      toast.error(msg);
    });
  }, [leadSelect]);

  const handleStateSelectOpen = useCallback(async () => {
    if (usCountryId) {
      stateSelect.load().catch((err) => console.error('Failed to load states:', err));
      return;
    }
    pendingStateLoadRef.current = true;
    try {
      const id = await fetchUnitedStatesId().unwrap();
      if (id) setUsCountryId(id);
    } catch (err) {
      console.error('Failed to load US country id:', err);
      pendingStateLoadRef.current = false;
    }
  }, [usCountryId, fetchUnitedStatesId, stateSelect]);

  useEffect(() => {
    if (usCountryId && pendingStateLoadRef.current) {
      pendingStateLoadRef.current = false;
      stateSelect.load(true).catch((err) => console.error('Failed to load states:', err));
    }
  }, [usCountryId, stateSelect]);

  // Update parent formData when local state changes
  useEffect(() => {
    if (updateFormData) {
      updateFormData({
        dba_name: dbaName,
        contact_name: contactName,
        contact_phone: contactPhone,
        ssn: ssnNumber,
        dba_phone: dbaPhone,
        dba_email: dbaEmail,
        fax_number: faxNumber,
        legal_name: legalName,
        legal_phone: legalPhone,
        legal_email: legalEmail,
        legal_fax_number: legalFaxNumber,
        country_code: countryCode,
        lead_id: selectedLead,
        source_type: sourceType,
        dba_address: {
          street: address,
          city: city,
          state: state,
          zip_code: zip,
          country: 'US'
        },
        legal_address: {
          street: legalAddress,
          city: legalCity,
          state: legalState,
          zip_code: legalZip,
          country: 'US'
        },
        submission_date: submissionDate,
        monthly_volume: monthlyVolume,
        monthly_lease: monthlyLease,
        is_bundled: isBundled,
        processing_offered: processingOffered,
        highest_ticket: highestTicket,
        fns_number_for_ebt: fnsNumberForEbt,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [
    dbaName, contactName, contactPhone, dbaPhone, dbaEmail, faxNumber, legalName,
    legalPhone, legalEmail, legalFaxNumber, countryCode, selectedLead,
    address, city, state, zip, ssnNumber,
    legalAddress, legalCity, legalState, legalZip, sourceType,
    submissionDate, monthlyVolume, monthlyLease, isBundled, processingOffered,
    highestTicket, fnsNumberForEbt,
  ]);

  // Sync local state with formData prop when navigating between steps
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0 && !isInitialized.current) {
      setSelectedLead(formData.lead_id || '');
      setSsnNumber(formData?.ssn || '');
      setDbaName(formData.dba_name || '');
      setContactName(formData.contact_name || '');
      setContactPhone(formData.contact_phone || '');
      setDbaPhone(formData.dba_phone || '');
      setDbaEmail(formData.dba_email || '');
      setFaxNumber(formData.fax_number || '');
      setLegalName(formData.legal_name || '');
      setLegalPhone(formData.legal_phone || '');
      setLegalEmail(formData.legal_email || '');
      setLegalFaxNumber(formData.legal_fax_number || '');
      setCountryCode(formData.country_code || '');
      setSelectedLead(formData.lead_id || ''); // Sync lead selection
      setAddress(formData.dba_address?.street || '');
      setCity(formData.dba_address?.city || '');
      setState(formData.dba_address?.state || '');
      setZip(formData.dba_address?.zip_code || '');
      setLegalAddress(formData.legal_address?.street || '');
      setLegalCity(formData.legal_address?.city || '');
      setLegalState(formData.legal_address?.state || '');
      setLegalZip(formData.legal_address?.zip_code || '');
      setSubmissionDate(formData.submission_date || '');
      setMonthlyVolume(formData.monthly_volume ?? '');
      setMonthlyLease(formData.monthly_lease ?? '');
      setIsBundled(Boolean(formData.is_bundled));
      setProcessingOffered(formData.processing_offered ?? '');
      setHighestTicket(formData.highest_ticket ?? '');
      setFnsNumberForEbt(formData.fns_number_for_ebt ?? '');
      isInitialized.current = true;
    }
  }, [formData]);

  useEffect(() => {
    if (sourceType === 'lead' && selectedLeadId) {
      // Fetch lead details and pre-fill the form
      fetchLeadById(selectedLeadId).unwrap().then((response) => {
        const leadData = response.data?.data;
        if (leadData) {
          // Pre-fill form with lead data
          setDbaName(leadData.name || '');
          setContactName(leadData.name || '');
          setDbaEmail(leadData.email || '');
          setDbaPhone(leadData.phone || '');
          // You can pre-fill other fields as needed
        }
      }).catch(error => {
        console.error("Error fetching lead details:", error);
      });
    }
  }, [sourceType, selectedLeadId, fetchLeadById]);

  const handleCopyDbaToLegal = () => {
    setLegalName(dbaName);
    setLegalAddress(address);
    setLegalCity(city);
    setLegalState(state);
    setLegalZip(zip);
    setLegalPhone(dbaPhone);
    setLegalEmail(dbaEmail);
    setLegalFaxNumber(faxNumber);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Lead Selection Section */}
      {sourceType === 'normal' && (
        <FormSection
          icon={<MailIcon />}
          title="Lead Information"
          bgColor="green.50"
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 3 }}>
            <FormSelect
              label="Select Lead"
              value={selectedLead}
              name="lead_id"
              onChange={(e) => setSelectedLead(e.target.value)}
              options={leadSelect.options}
              placeholder="Search and select a lead..."
              searchable={true} // Enable search functionality
              required={true} // Set to true if this field is required
              error={!!validationErrors.lead_id}
              errorMessage={validationErrors.lead_id}
              onOpen={handleLeadSelectOpen}
              loading={leadSelect.loading}
            />
          </Box>
        </FormSection>)}
      {sourceType === 'lead' && (
        <FormSection
          icon={<MailIcon />}
          title="Lead Source Information"
          bgColor="green.50"
        >
          <Box sx={{ p: 2, bgcolor: 'green.100', borderRadius: 1 }}>
            <Typography variant="body2" color="green.800">
              This merchant is being added from a lead. Lead ID: {selectedLeadId}
            </Typography>
          </Box>
        </FormSection>
      )}

      {/* DBA Information Section */}
      <FormSection
        icon={<BusinessIcon />}
        title="DBA Information"
        bgColor="blue.50"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <SimpleInput
            type="text"
            name="dba[name]"
            value={dbaName}
            onChange={(e) => setDbaName(e.target.value)}
            placeholder="Enter DBA Name"
            label="DBA Name"
            required={true}
            error={!!validationErrors.dba_name}
            errorMessage={validationErrors.dba_name}
          />

           <SimpleInput
            type="text"
            name="dba[ssn]"
            value={ssnNumber}
            onChange={(e) => setSsnNumber(e.target.value)}
            placeholder="Enter SSN Number"
            label="SSN Number"
            maxLength="9"
            required={true}
            error={!!validationErrors.ssn}
            errorMessage={validationErrors.ssn}
          />
          <SimpleInput
            type="text"
            name="dba[address]"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Enter Address"
            label="Address"
            required={true}
            error={!!validationErrors.dba_address_street}
            errorMessage={validationErrors.dba_address_street}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="text"
            name="dba[zip]"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="Enter Zip Code"
            label="Zip"
            required={true}
            error={!!validationErrors.dba_address_zip_code}
            errorMessage={validationErrors.dba_address_zip_code}
          />
          <SimpleInput
            type="text"
            name="dba[phone]"
            value={dbaPhone}
            onChange={(e) => setDbaPhone(e.target.value)}
            placeholder="Enter DBA Phone"
            label="DBA Phone"
            required={true}
            error={!!validationErrors.dba_phone}
            errorMessage={validationErrors.dba_phone}
          />
          <SimpleInput
            type="text"
            name="contacts[name]"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Enter Contact Name"
            label="Contact Name"
          />
        </Box>
        <Box sx={{ display: 'grid', alignItems: 'center', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="text"
            name="dba[city]"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Enter City"
            label="City"
            required={true}
            error={!!validationErrors.dba_address_city}
            errorMessage={validationErrors.dba_address_city}
          />
          <Box sx={{ mt: '30px' }}>
            <FormSelect
              label="Select State"
              value={state}
              name="dba[state]"
              onChange={(e) => setState(e.target.value)}
              options={stateOptions}
              placeholder="Select State"
              searchable={true}
              required={true}
              error={!!validationErrors.dba_address_state}
              errorMessage={validationErrors.dba_address_state}
              onOpen={handleStateSelectOpen}
              loading={stateSelect.loading}
            />
          </Box>
          <SimpleInput
            type="email"
            name="dba[email]"
            value={dbaEmail}
            onChange={(e) => setDbaEmail(e.target.value)}
            placeholder="Enter DBA Email"
            label="DBA Email"
            required={true}
            error={!!validationErrors.dba_email}
            errorMessage={validationErrors.dba_email}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="text"
            name="dba[fax_phone]"
            value={faxNumber}
            onChange={(e) => setFaxNumber(e.target.value)}
            placeholder="Enter Fax Number"
            label="Fax Number"
          />
          <SimpleInput
            type="text"
            name="contact[phone]"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="Contact Phone"
            label={'Contact Phone'}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'grey.700', fontWeight: 600 }}>
          Additional DBA details
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mt: 1 }}>
          <SimpleInput
            type="date"
            name="dba[submission_date]"
            value={submissionDate}
            onChange={(e) => setSubmissionDate(e.target.value)}
            label="Submission date"
          />
          <SimpleInput
            type="text"
            name="dba[monthly_volume]"
            value={monthlyVolume}
            onChange={(e) => setMonthlyVolume(e.target.value)}
            placeholder="e.g. $50,000"
            label="Monthly volume"
          />
          <SimpleInput
            type="text"
            name="dba[monthly_lease]"
            value={monthlyLease}
            onChange={(e) => setMonthlyLease(e.target.value)}
            placeholder="Monthly lease"
            label="Monthly lease"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isBundled}
                onChange={(e) => setIsBundled(e.target.checked)}
                name="dba[is_bundled]"
              />
            }
            label="Is bundled"
          />
          <SimpleInput
            type="text"
            name="dba[processing_offered]"
            value={processingOffered}
            onChange={(e) => setProcessingOffered(e.target.value)}
            placeholder="Processing offered"
            label="Processing offered"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="text"
            name="dba[highest_ticket]"
            value={highestTicket}
            onChange={(e) => setHighestTicket(e.target.value)}
            placeholder="Highest ticket"
            label="Highest ticket"
          />
          <SimpleInput
            type="text"
            name="dba[fns_number_for_ebt]"
            value={fnsNumberForEbt}
            onChange={(e) => setFnsNumberForEbt(e.target.value)}
            placeholder="FNS number for EBT"
            label="FNS number for EBT"
          />
        </Box>
      </FormSection>

      {/* Corporate / Legal Information Section */}
      <FormSection
        icon={<GavelIcon />}
        title="Corporate / Legal Information"
        bgColor="grey.50"
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <CustomButton
            variant="contained"
            color="secondary"
            onClick={handleCopyDbaToLegal}
            customStyles={{
              bgcolor: 'grey.200',
              color: 'grey.700',
              '&:hover': { bgcolor: 'grey.300' },
            }}
          >
            Copy DBA Information to Legal
          </CustomButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <SimpleInput
            type="text"
            name="legal[name]"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder="Enter Legal Name"
            label="Legal Name"
          />
          <SimpleInput
            type="text"
            name="legal[address]"
            value={legalAddress}
            onChange={(e) => setLegalAddress(e.target.value)}
            placeholder="Enter Address"
            label="Address"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="text"
            name="legal[zip]"
            value={legalZip}
            onChange={(e) => setLegalZip(e.target.value)}
            placeholder="Enter Zip Code"
            label="Zip"
          />
          <SimpleInput
            type="text"
            name="legal[phone]"
            value={legalPhone}
            onChange={(e) => setLegalPhone(e.target.value)}
            placeholder="Enter Legal Phone"
            label="Legal Phone"
          />
          <SimpleInput
            type="text"
            name="legal[city]"
            value={legalCity}
            onChange={(e) => setLegalCity(e.target.value)}
            placeholder="Enter City"
            label="City"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mt: 2 }}>
          <Box sx={{ mt: '30px' }}>
            <FormSelect
              label="State"
              name="legal[state_id]"
              value={legalState}
              onChange={(e) => setLegalState(e.target.value)}
              options={stateOptions}
              placeholder="State"
              onOpen={handleStateSelectOpen}
              loading={stateSelect.loading}
            />
          </Box>
          <SimpleInput
            type="email"
            name="legal[email]"
            value={legalEmail}
            onChange={(e) => setLegalEmail(e.target.value)}
            placeholder="Enter Legal Email"
            label="Legal Email"
          />
          <SimpleInput
            type="text"
            name="legal[fax_phone]"
            value={legalFaxNumber}
            onChange={(e) => setLegalFaxNumber(e.target.value)}
            placeholder="Enter Fax Number"
            label="Fax Number"
          />
        </Box>
      </FormSection>
    </Box>
  );
};

export default MainStep;